import type {
	AddBatchInput,
	CancelItemInput,
	CreateOrderInput,
	KotStatus,
	OrderType,
} from "@luckymenu/core";
import type { KeyValueStorage } from "../adapters";
import type { DraftLine } from "./draft";
import type { OrderingProblem } from "./problems";

/**
 * Changes on their way to the server (OP-17, P3-T29). "Send to kitchen",
 * "Cancel item" and a KOT's "Done" go here at once; the screens show them
 * as done or on their way, and the outbox sends them one at a time, oldest
 * first, for the active restaurant, each with the request ID made when staff
 * acted, so a retry after a lost answer never makes a second batch or a
 * second cancel (api.md, Idempotency). Without a connection it waits, and
 * retries after 1, 2, 4 ... 30 seconds, or at once when the device comes
 * back online or the socket reconnects. Entries are kept in the platform's
 * storage, so a reload or an app restart sends them later. A refusal from
 * the server (not a lost connection) stops that entry with its problem
 * until staff try again or discard it.
 */

type Common = {
	/**
	 * The request ID: the batch's or the cancel's `clientRequestId`
	 * (`kot:<kotId>:<status>` for a KOT). Kept for every retry.
	 */
	id: string;
	restaurantId: string;
	/** The order it changes; client-made for a new order. */
	orderId: string;
	createdAt: string;
	/**
	 * `queued`: waiting its turn, or to retry after `problem` (no
	 * connection); `sending`: on its way; `failed`: refused by the server,
	 * until retried or dismissed.
	 */
	state: "queued" | "sending" | "failed";
	attempts: number;
	problem: OrderingProblem | null;
	/** For `tableTaken`: the table's open order. */
	takenBy: string | null;
	/** Epoch ms before which a queued entry is not tried again. */
	nextAttemptAt: number | null;
};

/** Items for the kitchen, with what the screens show until the server answers. */
type SendFields = { lines: DraftLine[]; tableLabel: string | null; orderType: OrderType };

export type SendEntry = Common &
	SendFields &
	({ kind: "createOrder"; input: CreateOrderInput } | { kind: "addBatch"; input: AddBatchInput });
export type CancelItemEntry = Common & {
	kind: "cancelItem";
	input: CancelItemInput;
	/** For the screens. */
	itemName: string;
};
export type KotStatusEntry = Common & {
	kind: "setKotStatus";
	input: { kotId: string; status: KotStatus };
};

export type OutboxEntry = SendEntry | CancelItemEntry | KotStatusEntry;
export type OutboxKind = OutboxEntry["kind"];

type Managed = "state" | "attempts" | "problem" | "takenBy" | "createdAt" | "nextAttemptAt";
type NewOf<T> = T extends unknown ? Omit<T, Managed> & { createdAt?: string } : never;
export type NewOutboxEntry = NewOf<OutboxEntry>;

export function isSend(entry: OutboxEntry): entry is SendEntry {
	return entry.kind === "createOrder" || entry.kind === "addBatch";
}

/** The ID of a KOT change: one per ticket and status, so repeats join. */
export function kotChangeId(kotId: string, status: KotStatus): string {
	return `kot:${kotId}:${status}`;
}

export type SendFailure = {
	problem: OrderingProblem;
	/** Worth trying again later: no connection, a server hiccup, too many requests. */
	retryable: boolean;
	takenBy: string | null;
};

export type OutboxOptions = {
	/** Sends one entry; resolves with the server's answer. */
	send(entry: OutboxEntry): Promise<unknown>;
	/** Turns a thrown error into a problem. */
	failure(error: unknown): SendFailure;
	/** Entries of other restaurants wait until that restaurant is active again. */
	activeRestaurant(): string | null;
	/** False while the device knows it has no network: nothing is tried. */
	online?(): boolean;
	/** Keeps the entries across reloads and restarts. */
	storage?: KeyValueStorage;
	now?: () => number;
};

export const OUTBOX_STORAGE_KEY = "luckymenu.outbox";
const MAX_DELAY_MS = 30_000;

/** 1 s, 2 s, 4 s ... at most 30 s after the `attempts`th try. */
export function retryDelayMs(attempts: number): number {
	return Math.min(MAX_DELAY_MS, 1000 * 2 ** Math.max(0, attempts - 1));
}

/** Reads stored entries; one on its way when the app stopped is queued again. */
export function parseOutbox(raw: string | null): OutboxEntry[] {
	if (!raw) return [];
	try {
		const value = JSON.parse(raw) as unknown;
		if (!Array.isArray(value)) return [];
		return value
			.filter(
				(e): e is OutboxEntry =>
					typeof e === "object" &&
					e !== null &&
					typeof (e as OutboxEntry).id === "string" &&
					typeof (e as OutboxEntry).restaurantId === "string" &&
					["createOrder", "addBatch", "cancelItem", "setKotStatus"].includes(
						(e as OutboxEntry).kind,
					),
			)
			.map((e) => (e.state === "sending" ? { ...e, state: "queued", nextAttemptAt: null } : e));
	} catch {
		return [];
	}
}

type Listener = () => void;
type SentListener = (entry: OutboxEntry, result: unknown) => void;

export class Outbox {
	private entries: OutboxEntry[] = [];
	private readonly listeners = new Set<Listener>();
	private readonly sentListeners = new Set<SentListener>();
	private readonly options: OutboxOptions;
	private flushing = false;
	private loaded = false;
	private timer: ReturnType<typeof setTimeout> | undefined;
	private writing: Promise<void> = Promise.resolve();

	constructor(options: OutboxOptions) {
		this.options = options;
		if (!options.storage) this.loaded = true;
	}

	private now(): number {
		return this.options.now?.() ?? Date.now();
	}

	/** True once stored entries have been read (at once without storage). */
	isLoaded(): boolean {
		return this.loaded;
	}

	/** Reads the stored entries, keeping any added meanwhile, then starts sending. */
	async load(): Promise<void> {
		if (this.loaded || !this.options.storage) return;
		const stored = parseOutbox(
			await this.options.storage.getItem(OUTBOX_STORAGE_KEY).catch(() => null),
		);
		const known = new Set(this.entries.map((e) => e.id));
		this.loaded = true;
		this.set([...stored.filter((e) => !known.has(e.id)), ...this.entries]);
		void this.flush();
	}

	/** Every entry, oldest first. The same array until something changes. */
	getEntries(): readonly OutboxEntry[] {
		return this.entries;
	}

	subscribe(listener: Listener): () => void {
		this.listeners.add(listener);
		return () => this.listeners.delete(listener);
	}

	/** Called with the server's answer after each entry is through. */
	onSent(listener: SentListener): () => void {
		this.sentListeners.add(listener);
		return () => this.sentListeners.delete(listener);
	}

	/**
	 * Queues an entry and starts sending. An ID already there is ignored: no
	 * duplicates. A KOT change replaces the opposite one still waiting.
	 */
	add(entry: NewOutboxEntry): void {
		if (this.entries.some((e) => e.id === entry.id)) return;
		let entries = this.entries;
		if (entry.kind === "setKotStatus") {
			entries = entries.filter(
				(e) =>
					!(
						e.kind === "setKotStatus" &&
						e.input.kotId === entry.input.kotId &&
						e.state !== "sending"
					),
			);
		}
		const full = {
			...entry,
			createdAt: entry.createdAt ?? new Date(this.now()).toISOString(),
			state: "queued",
			attempts: 0,
			problem: null,
			takenBy: null,
			nextAttemptAt: null,
		} as OutboxEntry;
		this.set([...entries, full]);
		void this.flush();
	}

	/** Tries an entry again now, with the same request ID. */
	retry(id: string): void {
		this.update(id, (e) =>
			e.state === "sending" ? e : { ...e, state: "queued", nextAttemptAt: null },
		);
		void this.flush();
	}

	/**
	 * Replaces an entry that is not on its way, such as a new order for a
	 * table that got an order meanwhile, which goes to that order instead.
	 * Keeps its place.
	 */
	replace(id: string, entry: NewOutboxEntry): void {
		const at = this.entries.findIndex((e) => e.id === id);
		const old = this.entries[at];
		if (!old || old.state === "sending") return;
		const next = [...this.entries];
		next[at] = {
			...entry,
			createdAt: old.createdAt,
			state: "queued",
			attempts: 0,
			problem: null,
			takenBy: null,
			nextAttemptAt: null,
		} as OutboxEntry;
		this.set(next);
		void this.flush();
	}

	/** Gives up on an entry that is not on its way. */
	dismiss(id: string): void {
		const entry = this.entries.find((e) => e.id === id);
		if (!entry || entry.state === "sending") return;
		this.set(this.entries.filter((e) => e.id !== id));
	}

	/** The device is back online, the app is in front again, or the socket reconnected: try now. */
	wake(): void {
		if (this.entries.some((e) => e.state === "queued" && e.nextAttemptAt !== null))
			this.set(this.entries.map((e) => (e.state === "queued" ? { ...e, nextAttemptAt: null } : e)));
		void this.flush();
	}

	/** Stops the retry timer (tests, sign-out). */
	stop(): void {
		if (this.timer) clearTimeout(this.timer);
		this.timer = undefined;
	}

	/**
	 * Sends queued entries of the active restaurant, oldest first, one at a
	 * time. An entry waiting to retry holds back the ones after it (a new
	 * order goes before the items added to it).
	 */
	async flush(): Promise<void> {
		if (this.flushing || !this.loaded) return;
		this.flushing = true;
		try {
			for (;;) {
				if (this.options.online && !this.options.online()) return;
				const restaurantId = this.options.activeRestaurant();
				const next = this.entries.find(
					(e) => e.state === "queued" && e.restaurantId === restaurantId,
				);
				if (!next) return;
				if (next.nextAttemptAt !== null && next.nextAttemptAt > this.now()) {
					this.schedule(next.nextAttemptAt);
					return;
				}
				await this.sendOne(next);
			}
		} finally {
			this.flushing = false;
		}
	}

	private schedule(at: number): void {
		this.stop();
		this.timer = setTimeout(
			() => {
				this.timer = undefined;
				void this.flush();
			},
			Math.max(0, at - this.now()),
		);
	}

	private async sendOne(entry: OutboxEntry): Promise<void> {
		this.update(entry.id, (e) => ({ ...e, state: "sending", attempts: e.attempts + 1 }));
		try {
			const result = await this.options.send(entry);
			const sent = this.entries.find((e) => e.id === entry.id) ?? entry;
			this.set(this.entries.filter((e) => e.id !== entry.id));
			for (const listener of this.sentListeners) listener(sent, result);
		} catch (error) {
			const failure = this.options.failure(error);
			this.update(entry.id, (e) =>
				failure.retryable
					? {
							...e,
							state: "queued",
							problem: failure.problem,
							nextAttemptAt: this.now() + retryDelayMs(e.attempts),
						}
					: { ...e, state: "failed", problem: failure.problem, takenBy: failure.takenBy },
			);
		}
	}

	private update(id: string, change: (entry: OutboxEntry) => OutboxEntry): void {
		this.set(this.entries.map((e) => (e.id === id ? change(e) : e)));
	}

	private set(entries: OutboxEntry[]): void {
		this.entries = entries;
		const storage = this.options.storage;
		if (storage && this.loaded) {
			// Writes in order, so a slow write never overwrites a newer one.
			const value = JSON.stringify(entries);
			this.writing = this.writing
				.then(() =>
					entries.length === 0
						? storage.removeItem(OUTBOX_STORAGE_KEY)
						: storage.setItem(OUTBOX_STORAGE_KEY, value),
				)
				.catch(() => {});
		}
		for (const listener of this.listeners) listener();
	}
}

/** Sends to the kitchen for one order, oldest first. */
export function sendsFor(entries: readonly OutboxEntry[], orderId: string): SendEntry[] {
	return entries.filter((e): e is SendEntry => isSend(e) && e.orderId === orderId);
}

/** Entries for one order, oldest first. */
export function entriesFor(entries: readonly OutboxEntry[], orderId: string): OutboxEntry[] {
	return entries.filter((e) => e.orderId === orderId);
}

/** Entries waiting or on their way, and refused ones, for the offline banner. */
export function outboxCounts(entries: readonly OutboxEntry[], restaurantId: string | null) {
	const mine = entries.filter((e) => e.restaurantId === restaurantId);
	return {
		waiting: mine.filter((e) => e.state !== "failed").length,
		failed: mine.filter((e) => e.state === "failed").length,
	};
}
